import type { IPagedResponse } from "@atlas/sdk";
import type { IUserInfo } from "@models";
import { AdministerUserStatus } from "@models";
import { useCompanyUsersInfinite } from "@query";
import { APP_BASE_PATH } from "@shared";
import { useMemo } from "react";

const EMPTY_PAGES: IPagedResponse<IUserInfo>[] = [];

interface IUseAdministerUsersOptions {
  page?: number;
  pageSize?: number;
  emailSearch?: string;
}

export function useAdministerUsers(
  companyUuid: string | undefined,
  options?: IUseAdministerUsersOptions,
) {
  const { page = 0, pageSize = 10, emailSearch } = options ?? {};
  const { data, isLoading } = useCompanyUsersInfinite(
    { companyUuid: companyUuid ?? "", size: pageSize, page, emailSearch },
    APP_BASE_PATH,
  );

  const pages =
    (data as { pages?: IPagedResponse<IUserInfo>[] } | undefined)?.pages ??
    EMPTY_PAGES;
  const rawUsers: IUserInfo[] = useMemo(
    () => pages.flatMap((p: IPagedResponse<IUserInfo>) => p.content ?? []),
    [pages],
  );

  const users = useMemo(() => {
    return rawUsers.map((u) => ({
      id: u.id,
      username: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.loginId,
      firstName: u.firstName ?? "",
      lastName: u.lastName ?? "",
      initial: (
        u.firstName?.trim().charAt(0) ??
        u.loginId?.charAt(0) ??
        "U"
      ).toUpperCase(),
      loginId: u.loginId,
      email: u.email,
      joinedDate: u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "",
      role: u.userType ?? "User",
      application: "DXP",
      status: u.isActive
        ? AdministerUserStatus.Active
        : AdministerUserStatus.Inactive,
      isActive: u.isActive,
    }));
  }, [rawUsers]);

  const lastPage = pages[pages.length - 1];
  const totalElements = lastPage?.totalElements ?? 0;
  const totalPages = lastPage?.totalPages ?? 0;

  return {
    users,
    rawUsers,
    totalElements,
    totalPages,
    isLoading,
  };
}
